<template>
  <div>
    <v-dialog v-model="dialog" width="auto" @click:outside="closeDialog">
      <v-form ref="editForm" v-model="valid" lazy-validation>
        <v-card>
          <v-card-title class="headline footer">
            <span>{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-alert
                text
                dense
                color="blue"
                icon="mdi-information-outline"
                border="left"
              >
                If there are more than one sub-brand, then write them dividing
                by commas.
              </v-alert>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    outlined
                    dense
                    type="text"
                    v-model="brandName"
                    label="Brand name"
                    :rules="[rules.required]"
                    :disabled="formTitle.includes('Edit')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    outlined
                    dense
                    type="text"
                    v-model="subBrand"
                    label="Sub-brand"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-combobox
                    label="Original Currency"
                    v-model="originalCurrency"
                    type="text"
                    :items="currencies"
                    :rules="[rules.required]"
                    outlined
                    dense
                  >
                  </v-combobox>
                </v-col>
              </v-row>
              <span class="accent--text">Currency values</span>

              <v-row class="pt-2">
                <v-col
                  cols="12"
                  sm="3"
                  md="2"
                  v-for="item in allCurrencyRates"
                  :key="item.RRPname"
                >
                  <v-text-field
                    v-model="item.value"
                    :label="item.RRPname"
                    type="number"
                    min="0"
                    :rules="
                      item.required
                        ? [rules.required, rules.number]
                        : [rules.number]
                    "
                    outlined
                    dense
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <span class="accent--text">Brand stories</span>

              <div
                v-for="(stories, key) of brandStories"
                :key="key"
                class="mt-4"
              >
                <v-row
                  ><v-col class="">
                    <v-sheet color="footer" class="pl-2 font-weight-medium">
                      <v-row>
                        <v-col cols="auto" class="py-0">
                          {{ formatLanguageName(key) }}
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col cols="auto" class="py-0">
                          <v-icon small @click="deleteLanguageStories(key)">
                            mdi-close-thick</v-icon
                          >
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </v-col></v-row
                >
                <v-row>
                  <v-col class="pb-0">
                    <v-row>
                      <v-col
                        v-for="(story, i) in stories"
                        :key="i"
                        cols="12"
                        sm="3"
                        md="2"
                        lg="6"
                      >
                        <v-row>
                          <v-col cols="10">
                            <v-textarea
                              :value="story"
                              v-model="stories[i]"
                              outlined
                              dense
                              :rules="[rules.requiredText]"
                            >
                            </v-textarea>
                          </v-col>
                          <v-col cols="1">
                            <v-icon
                              color="error"
                              @click="deleteBrandStory(key, i)"
                              >mdi-close-thick</v-icon
                            >
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row class="pb-0 my-0">
                  <v-col class="pt-0">
                    <v-tooltip right>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="success"
                          v-bind="attrs"
                          v-on="on"
                          @click="addBrandStory(key)"
                          >mdi-plus-circle</v-icon
                        >
                      </template>
                      <span>Add field</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </div>
              <v-row class="mt-2">
                <v-col cols="5">
                  <v-select
                    ref="language"
                    v-model="language"
                    outlined
                    dense
                    :items="languages"
                    clearable
                    label="Select language to add new brand story..."
                    placeholder="Select language to add brand story..."
                    :item-text="'text'"
                    :item-value="'value'"
                    @change="addNewLanguageStory(language)"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" outlined @click="closeDialog"> Cancel </v-btn>
            <v-btn color="success" outlined @click="save"> Save </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-----------------STATUS DIALOG, WHEN DOING STUFF WITH BRANDS---------------->
    <v-dialog persistent v-model="statusDialog" width="unset">
      <status-info
        :responseMessage="responseMessage"
        :filename="filename"
        :pending="pending"
        @close="closeStatusDialog"
        :textColor="textColor"
      ></status-info>
    </v-dialog>
    <!---------------------------------------------------------------------------------->
  </div>
</template>

<script>
import StatusInfo from "../organisms/StatusInfo.vue";
import Vue from "vue";

export default {
  name: "BrandForm",
  components: {
    StatusInfo,
  },
  props: {
    brand: Object,
    formTitle: String,
  },
  data: () => ({
    brandName: "",
    originalCurrency: "",
    subBrand: "",
    currencyRates: [],
    allCurrencyRates: [],
    brandStories: {},
    responseMessage: "",
    textColor: "basic--text",
    filename: "",
    statusDialog: false,
    pending: false,
    dialog: false,
    valid: true,
    rules: {
      required: (value) => !!value || "Field is required.",
      requiredText: (value) =>
        !!value || "Required! Fill in text or remove field.",
      number: (value) => {
        if (value < 0) {
          return "Number must be more than 0!";
        } else {
          return true;
        }
      },
    },
    currencies: [],
    languages: [
      { text: "English", value: "en" },
      { text: "Lithuanian", value: "lt" },
      { text: "French", value: "fr" },
      { text: "Italian", value: "it" },
      { text: "Spanish", value: "es" },
      { text: "German", value: "de" },
      { text: "Latvian", value: "lv" },
      { text: "Estonian", value: "ee" },
    ],
    language: "",
  }),
  watch: {
    brandStories(val) {
      this.languages = [
        { text: "English", value: "en" },
        { text: "Lithuanian", value: "lt" },
        { text: "French", value: "fr" },
        { text: "Italian", value: "it" },
        { text: "Spanish", value: "es" },
        { text: "German", value: "de" },
        { text: "Latvian", value: "lv" },
        { text: "Estonian", value: "ee" },
      ];
      if (val) {
        for (const [key] of Object.entries(val)) {
          this.languages = this.languages.filter((obj) => {
            return obj.value !== key;
          });
        }
      }
    },
  },
  methods: {
    closeStatusDialog() {
      this.statusDialog = false;
      this.$emit("closeBrandForm", true);
    },
    closeDialog() {
      this.$emit("closeBrandForm");
    },
    async openDialog() {
      this.statusDialog = true;
      this.pending = true;
      await this.getCurrencies();
      await this.getAllCurrencyRates();
      await this.fillFields();
      await this.fillCurrencyRates();

      this.statusDialog = false;
      this.dialog = true;
    },
    async getCurrencies() {
      try {
        const response = await this.$store.dispatch("getCurrencies");
        this.currencies = response.data.currency;
      } catch (error) {
        console.log(error);
      }
    },
    async getAllCurrencyRates() {
      try {
        const response = await this.$store.dispatch("getAllCurrencyRates");
        this.allCurrencyRates = response.data.AllCurrencyRates;
      } catch (error) {
        console.log(error);
      }
    },
    async fillFields() {
      for (const [key, value] of Object.entries(this.brand)) {
        this[key] = value;
      }
    },
    async fillCurrencyRates() {
      for (let i = 0; i < this.allCurrencyRates.length; i++) {
        const element = this.allCurrencyRates[i];
        let found = this.currencyRates.find(
          (item) => item.RRPname === element.RRPname
        );

        if (found) {
          element["value"] = found.value;
        } else {
          element["value"] = "";
        }
      }
    },
    deleteBrandStory(language, index) {
      this.brandStories[language].splice(index, 1);
    },
    deleteLanguageStories(key) {
      Vue.delete(this.brandStories, key);
    },
    addBrandStory(language) {
      this.brandStories[language].push("");
    },
    addNewLanguageStory(language) {
      Vue.set(this.brandStories, language);
      this.brandStories[language] = [];
      this.$refs.language.reset();
    },
    formatLanguageName(key) {
      let string = "";
      switch (key) {
        case "en":
          string = "English";
          break;
        case "fr":
          string = "French";
          break;
        case "it":
          string = "Italian";
          break;
        case "es":
          string = "Spanish";
          break;
        case "de":
          string = "German";
          break;
        case "lv":
          string = "Latvian";
          break;
        case "ee":
          string = "Estonian";
          break;
        case "lt":
          string = "Lithuanian";
          break;
        default:
          string = key;
          break;
      }
      return string;
    },
    save() {
      if (this.$refs.editForm.validate()) {
        this.pending = true;
        this.statusDialog = true;
        this.dialog = false;

        if (this.formTitle.includes("Edit")) {
          this.putBrandToServer();
        }
        if (this.formTitle.includes("Add brand")) {
          this.postBrandToServer();
        }
      }
    },
    async putBrandToServer() {
      let data = {
        body: {
          brandName: this.brandName,
          subBrand: this.subBrand,
          originalCurrency: this.originalCurrency,
          currencyRates: this.allCurrencyRates,
          brandStories: this.brandStories,
        },
        id: this.brand.id,
      };

      try {
        await this.$store.dispatch("putBrand", data);
        this.textColor = "basic--text";
        this.pending = false;
        this.filename = "high_five.svg";
        this.responseMessage = `Success! Brand ${this.brandName} updated!`;
      } catch (error) {
        console.log(error);
        this.textColor = "red--text";
        this.pending = false;
        if (error.response) {
          this.showErrors(error.response.status, error.response.data);
        } else {
          // anything else
          this.filename = "feeling_blue.svg";
          this.responseMessage = `SORRY... Couldn't update brand ${this.brandName}. Something went wrong.`;
        }
      }
    },
    async postBrandToServer() {
      let data = {
        brandName: this.brandName,
        subBrand: this.subBrand,
        originalCurrency: this.originalCurrency,
        currencyRates: this.allCurrencyRates,
        brandStories: this.brandStories,
      };

      try {
        await this.$store.dispatch("postBrand", data);
        this.textColor = "basic--text";
        this.pending = false;
        this.filename = "high_five.svg";
        this.responseMessage = `Success! Brand ${this.brandName} created!`;
      } catch (error) {
        console.log(error);
        this.textColor = "red--text";
        this.pending = false;
        if (error.response) {
          this.showErrors(error.response.status, error.response.data);
        } else {
          // anything else
          this.filename = "feeling_blue.svg";
          this.responseMessage = `SORRY... Couldn't create brand ${this.brandName}. Something went wrong.`;
        }
      }
    },
    showErrors(status, message) {
      // client received an error response (5xx, 4xx)
      this.responseMessage = message;
      switch (status) {
        case 400:
          this.filename = "empty.svg";
          break;
        case 401:
          //clear local storage, push to login
          localStorage.clear();
          this.$router.push("login");
          break;
        case 403:
          this.filename = "403_forbidden.svg";
          break;
        case 404:
          this.responseMessage = "Bad request. Please contact TECH team.";
          this.filename = "void.png";
          break;
        case 500:
          this.filename = "server_down.svg";
      }
    },
  },
  async mounted() {
    this.openDialog();
  },
};
</script>

